import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ChatComposer from './ChatComposer';
import ChatHeader from './ChatHeader';
import MessageList from './MessageList';
import PrivateRecipientPicker from './PrivateRecipientPicker';
import useChatMessages from './useChatMessages';
import useModerationStatus from './useModerationStatus';
import usePresence from './usePresence';
import usePrivateNotifications from './usePrivateNotifications';
import usePlatformSettings from '../Admin/usePlatformSettings';
import supabase from '../../supabase/supabase';
import { REPORTS_TABLE } from '../../utils/auditLogger';
import { getClientId } from '../../utils/clientIdentity';

export default function ChatContainer({ currentUser, onChangeNickname, theme, toggleTheme }) {
  const [mode, setMode] = useState('public');
  const [recipient, setRecipient] = useState('');
  const [search, setSearch] = useState('');
  const [unreadByUser, setUnreadByUser] = useState({});
  const [clearedTimestamps, setClearedTimestamps] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('gallo_cleared_chats')) || {};
    } catch {
      return {};
    }
  });

  const processedMessageIdRef = useRef(null);
  const onlineUsers = usePresence(currentUser);
  const restriction = useModerationStatus(currentUser);
  const { messages, reactions, lastIncomingMessage, isSending, sendMessage, addReaction } = useChatMessages(currentUser);
  const { settings } = usePlatformSettings();

  const openPrivateConversation = useCallback((user) => {
    setMode('private');
    setRecipient(user);
    setSearch('');
    setUnreadByUser((current) => ({ ...current, [user]: 0 }));
  }, []);

  const { notificationsEnabled, toggleNotifications } = usePrivateNotifications({
    message: lastIncomingMessage,
    currentUser,
    activeRecipient: recipient,
    isPrivateChatOpen: mode === 'private',
    onOpenConversation: openPrivateConversation
  });

  useEffect(() => {
    if (!lastIncomingMessage || lastIncomingMessage.id === processedMessageIdRef.current) return;
    const isPrivateForCurrentUser =
      lastIncomingMessage.is_private &&
      lastIncomingMessage.recipient === currentUser &&
      lastIncomingMessage.nickname !== currentUser;

    if (isPrivateForCurrentUser) {
      const sender = lastIncomingMessage.nickname;
      const clearedAt = clearedTimestamps[sender];
      const isCleared = clearedAt && new Date(lastIncomingMessage.created_at) < new Date(clearedAt);

      if (!isCleared) {
        const isVisible = mode === 'private' && recipient === sender && document.visibilityState === 'visible';
        if (!isVisible) {
          setUnreadByUser((current) => ({ ...current, [sender]: (current[sender] || 0) + 1 }));
        }
      }
    }
    processedMessageIdRef.current = lastIncomingMessage.id;
  }, [lastIncomingMessage, currentUser, mode, recipient, clearedTimestamps]);

  const handleReportMessage = async (message) => {
    const reason = window.prompt(`Indica el motivo del reporte contra @${message.nickname}:`, 'Contenido inapropiado o spam');
    if (!reason) return;

    const { error } = await supabase.from(REPORTS_TABLE).insert({
      message_id: message.id,
      reported_nickname: message.nickname,
      reported_content: message.content,
      reporter_client_id: getClientId(),
      reporter_nickname: currentUser || 'Espectador',
      reason: reason.trim()
    });

    if (!error) {
      alert('Gracias. Tu reporte ha sido enviado a los moderadores de guardia.');
    }
  };

  const availableUsers = useMemo(() => {
    const names = new Set(onlineUsers);
    messages.forEach((message) => {
      names.add(message.nickname);
      if (message.recipient) names.add(message.recipient);
    });
    names.delete(currentUser);
    return [...names].filter(Boolean).sort((a, b) => (
      (unreadByUser[b] || 0) - (unreadByUser[a] || 0) || a.localeCompare(b)
    ));
  }, [messages, onlineUsers, currentUser, unreadByUser]);

  const visibleMessages = useMemo(() => {
    const term = search.trim().toLowerCase();
    return messages.filter((message) => {
      if (message.is_private) {
        const otherUser = message.nickname === currentUser ? message.recipient : message.nickname;
        const clearedAt = clearedTimestamps[otherUser];
        if (clearedAt && new Date(message.created_at) < new Date(clearedAt)) {
          return false;
        }
      }
      const belongsToView =
        mode === 'public'
          ? !message.is_private
          : recipient &&
            message.is_private &&
            ((message.nickname === currentUser && message.recipient === recipient) ||
              (message.nickname === recipient && message.recipient === currentUser));

      return (
        belongsToView &&
        (!term ||
          message.content.toLowerCase().includes(term) ||
          message.nickname.toLowerCase().includes(term))
      );
    });
  }, [messages, mode, recipient, currentUser, search, clearedTimestamps]);

  const unreadPrivateCount = Object.values(unreadByUser).reduce((total, count) => total + count, 0);

  const handleModeChange = (nextMode) => {
    setMode(nextMode);
    setSearch('');
    if (nextMode === 'private' && recipient) {
      setUnreadByUser((current) => ({ ...current, [recipient]: 0 }));
    }
  };

  const handleClearPrivateChat = () => {
    if (!recipient) return;
    const confirmed = window.confirm(`¿Seguro que deseas limpiar tu historial de chat con ${recipient}?`);
    if (!confirmed) return;
    const newTimestamps = { ...clearedTimestamps, [recipient]: new Date().toISOString() };
    setClearedTimestamps(newTimestamps);
    localStorage.setItem('gallo_cleared_chats', JSON.stringify(newTimestamps));
  };

  return (
    <div className="flex h-full flex-col bg-white dark:bg-[#0a0a0a] text-gray-900 dark:text-white transition-colors">
      <ChatHeader
        mode={mode}
        onModeChange={handleModeChange}
        onlineCount={onlineUsers.length}
        unreadPrivateCount={unreadPrivateCount}
        notificationsEnabled={notificationsEnabled}
        onToggleNotifications={toggleNotifications}
        search={search}
        onSearchChange={setSearch}
        onChangeNickname={onChangeNickname}
        theme={theme}
        toggleTheme={toggleTheme}
      />

      {mode === 'private' && (
        <PrivateRecipientPicker
          users={availableUsers}
          onlineUsers={onlineUsers}
          unreadByUser={unreadByUser}
          recipient={recipient}
          onRecipientChange={(user) => {
            if (user) openPrivateConversation(user);
            else setRecipient('');
          }}
          onClearChat={handleClearPrivateChat}
        />
      )}

      <MessageList
        messages={visibleMessages}
        reactions={reactions}
        currentUser={currentUser}
        mode={mode}
        recipient={recipient}
        onReact={addReaction}
        onReport={handleReportMessage}
      />

      <ChatComposer
        currentUser={currentUser}
        mode={mode}
        recipient={recipient}
        isSending={isSending}
        restriction={restriction}
        slowModeSeconds={settings?.slow_mode_seconds || 0}
        onSend={sendMessage}
        onRequestNickname={onChangeNickname}
      />
    </div>
  );
}